import {
  useState, useCallback,
} from 'react';
import {
  Check, Camera, AlertTriangle, Scale, DollarSign,
  ThermometerSun, Calendar, Package, ChevronDown, ChevronUp,
  X, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CameraCapture } from '@/components/ui/camera-capture';
import { cn } from '@/lib/classnames';
import type {
  Delivery, DeliveryLine 
} from '@/features/receiving/types';

interface ChecklistTabProps {
  readonly delivery: Delivery;
  readonly onUpdateLine: (lineId: string, updates: LineUpdate) => void;
  readonly onReportIssue: (lineId: string, issue: IssueReport) => void;
  readonly isUpdating?: boolean;
}

interface LineUpdate {
  receivedQuantity?: number;
  qualityStatus?: 'pending' | 'passed' | 'failed' | 'hold';
  temperature?: number;
  lotNumber?: string;
  expiryDate?: string;
  notes?: string;
}

interface IssueReport {
  type: 'damaged' | 'wrong_item' | 'quantity_mismatch' | 'quality_issue' | 'other';
  description: string;
  imageBase64?: string;
}

export function ChecklistTab({
  delivery,
  onUpdateLine,
  onReportIssue,
  isUpdating,
}: ChecklistTabProps) {
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState<string | null>(null);
  const [issueMode, setIssueMode] = useState<string | null>(null);

  const toggleExpand = (lineId: string) => {
    setExpandedLineId(expandedLineId === lineId ? null : lineId);
  };

  const handleMarkReceived = useCallback((line: DeliveryLine) => {
    onUpdateLine(line.lineId, {
      receivedQuantity: line.orderedQuantity,
      qualityStatus: 'passed',
    });
  }, [onUpdateLine]);

  const handleCaptureForLine = useCallback((
    lineId: string,
  ) => {
    // For now, just mark as received with the image
    // In a full implementation, this would trigger AI analysis
    onUpdateLine(lineId, {
      qualityStatus: 'passed',
      notes: 'Verified via photo',
    });
    setShowCamera(null);
  }, [onUpdateLine]);

  const handleReportIssue = useCallback((
    lineId: string,
    issueType: IssueReport['type'],
    imageBase64?: string
  ) => {
    onReportIssue(lineId, {
      type: issueType,
      description: `Issue reported: ${issueType.replace('_', ' ')}`,
      imageBase64,
    });
    setIssueMode(null);
    setShowCamera(null);
  }, [onReportIssue]);

  const completedCount = delivery.lines.filter(
    (l) => l.status === 'received'
  ).length;

  return (
    <div className="space-y-4">
      {/* Progress Summary */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold">Receiving Checklist</h3>
            <p className="text-sm text-gray-500">
              {completedCount} of {delivery.lines.length} items verified
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-600">
              {Math.round((completedCount / delivery.lines.length) * 100)}%
            </div>
            <p className="text-xs text-gray-500">Complete</p>
          </div>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all"
            style={{width: `${(completedCount / delivery.lines.length) * 100}%`,}}
          />
        </div>
      </Card>

      {/* Line Items Checklist */}
      <div className="space-y-3">
        {delivery.lines.map((line) => (
          <LineItemCard
            key={line.lineId}
            line={line}
            isExpanded={expandedLineId === line.lineId}
            showCamera={showCamera === line.lineId}
            issueMode={issueMode === line.lineId}
            isUpdating={isUpdating}
            onToggleExpand={() => toggleExpand(line.lineId)}
            onMarkReceived={() => handleMarkReceived(line)}
            onShowCamera={() => setShowCamera(line.lineId)}
            onHideCamera={() => setShowCamera(null)}
            onCapture={() => handleCaptureForLine(line.lineId)}
            onShowIssueMode={() => setIssueMode(line.lineId)}
            onHideIssueMode={() => setIssueMode(null)}
            onReportIssue={(type, img) => handleReportIssue(
              line.lineId,
              type,
              img
            )}
          />
        ))}
      </div>
    </div>
  );
}

interface LineItemCardProps {
  readonly line: DeliveryLine;
  readonly isExpanded: boolean;
  readonly showCamera: boolean;
  readonly issueMode: boolean;
  readonly isUpdating?: boolean;
  readonly onToggleExpand: () => void;
  readonly onMarkReceived: () => void;
  readonly onShowCamera: () => void;
  readonly onHideCamera: () => void;
  readonly onCapture: (imageBase64: string) => void;
  readonly onShowIssueMode: () => void;
  readonly onHideIssueMode: () => void;
  readonly onReportIssue: (
    type: IssueReport['type'],
    imageBase64?: string
  ) => void;
}

function LineItemCard({
  line,
  isExpanded,
  showCamera,
  issueMode,
  isUpdating,
  onToggleExpand,
  onMarkReceived,
  onShowCamera,
  onHideCamera,
  onCapture,
  onShowIssueMode,
  onHideIssueMode,
  onReportIssue,
}: LineItemCardProps) {
  const isReceived = line.status === 'received';
  const hasIssue = line.qualityStatus === 'failed' || line.qualityStatus === 'hold';

  return (
    <Card
      className={cn(
        'overflow-hidden transition-all',
        isReceived && 'bg-green-50 border-green-200',
        hasIssue && 'bg-amber-50 border-amber-200'
      )}
    >
      {/* Header - Always visible */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full p-4 flex items-center gap-3 text-left"
      >
        <StatusIcon isReceived={isReceived} hasIssue={hasIssue} />

        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{line.itemName}</p>
          <p className="text-sm text-gray-500">
            {line.sku} • {line.orderedQuantity} {line.unit}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isReceived && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              Verified
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t">
          {/* Camera View */}
          {showCamera && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Take Photo for Verification</p>
                <Button variant="ghost" size="sm" onClick={onHideCamera}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CameraCapture
                onCapture={(img) => onCapture(img)}
                aspectRatio="square"
              />
            </div>
          )}

          {/* Issue Reporting Mode */}
          {issueMode && !showCamera && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Report an Issue</p>
                <Button variant="ghost" size="sm" onClick={onHideIssueMode}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <IssueButton
                  label="Damaged"
                  onClick={() => onReportIssue('damaged')}
                />
                <IssueButton
                  label="Wrong Item"
                  onClick={() => onReportIssue('wrong_item')}
                />
                <IssueButton
                  label="Qty Mismatch"
                  onClick={() => onReportIssue('quantity_mismatch')}
                />
                <IssueButton
                  label="Quality Issue"
                  onClick={() => onReportIssue('quality_issue')}
                />
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={onShowCamera}
              >
                <Camera className="w-4 h-4 mr-2" />
                Take Photo of Issue
              </Button>
            </div>
          )}

          {/* Normal Expanded View */}
          {!showCamera && !issueMode && (
            <>
              {/* Details Grid */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <DetailItem
                  icon={Scale}
                  label="Quantity"
                  value={`${line.receivedQuantity ?? 0} / ${line.orderedQuantity} ${line.unit}`}
                />
                <DetailItem
                  icon={DollarSign}
                  label="Unit Price"
                  value="$12.50"
                />
                {line.temperature !== undefined && (
                  <DetailItem
                    icon={ThermometerSun}
                    label="Temperature"
                    value={`${line.temperature}°C`}
                  />
                )}
                {line.expiryDate && (
                  <DetailItem
                    icon={Calendar}
                    label="Expiry"
                    value={new Date(line.expiryDate).toLocaleDateString()}
                  />
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-4 flex gap-2">
                {!isReceived && (
                  <>
                    <Button
                      className="flex-1"
                      onClick={onMarkReceived}
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Mark Received
                    </Button>
                    <Button
                      variant="outline"
                      onClick={onShowCamera}
                    >
                      <Camera className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="text-amber-600"
                      onClick={onShowIssueMode}
                    >
                      <AlertTriangle className="w-4 h-4" />
                    </Button>
                  </>
                )}
                {isReceived && (
                  <div className="flex-1 text-center py-2 text-green-600 font-medium">
                    ✓ Item Verified
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function StatusIcon({
  isReceived,
  hasIssue,
}: {
  readonly isReceived: boolean;
  readonly hasIssue: boolean;
}) {
  const getBackgroundClass = () => {
    if (isReceived) return 'bg-green-500 text-white';
    if (hasIssue) return 'bg-amber-500 text-white';
    return 'bg-gray-200 text-gray-500';
  };

  const getIcon = () => {
    if (isReceived) return <Check className="w-5 h-5" />;
    if (hasIssue) return <AlertTriangle className="w-5 h-5" />;
    return <Package className="w-5 h-5" />;
  };

  return (
    <div
      className={cn(
        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
        getBackgroundClass()
      )}
    >
      {getIcon()}
    </div>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  readonly icon: typeof Scale;
  readonly label: string;
  readonly value: string;
}) {
  const IconToRender = icon;
  return (
    <div className="flex items-center gap-2 text-sm">
      <IconToRender className="w-4 h-4 text-gray-400" />
      <div>
        <p className="text-gray-500 text-xs">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  );
}

function IssueButton({
  label,
  onClick,
}: {
  readonly label: string;
  readonly onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="text-amber-600 border-amber-200 hover:bg-amber-50"
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
